import type { ChangeSetFilters } from './use-change-sets'

export type ResourceType = NonNullable<ChangeSetFilters['resourceType']>[number]

export interface HistoryArea {
  value: string
  label: string
  resourceTypes: readonly ResourceType[]
}

// Display order. carrier_issuer_mapping (global) and bulk_file (no entries yet) belong to no area.
export const AREAS: readonly HistoryArea[] = [
  { value: 'licensing', label: 'Licensing', resourceTypes: ['plan_entitlement', 'feature_entitlement'] },
  { value: 'privileged', label: 'Privileged', resourceTypes: ['carrier_entitlement'] },
  { value: 'network', label: 'Network', resourceTypes: ['network_entitlement'] },
  { value: 'applications', label: '3scale applications', resourceTypes: ['account_identifier'] },
  { value: 'machine_users', label: 'Machine users', resourceTypes: ['service_account'] },
  { value: 'users', label: 'Users', resourceTypes: ['user'] },
  {
    value: 'account',
    label: 'Account',
    resourceTypes: ['account', 'account_domain', 'permission_set', 'duplicate_candidate'],
  },
]

// Empty means every area; the filter then shows "All areas".
export const DEFAULT_AREAS: readonly string[] = []

export const PERIODS = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
] as const

export type HistoryPeriod = (typeof PERIODS)[number]['value']

export interface HistoryParams {
  areas: string[]
  set?: string
  period?: HistoryPeriod
  page: number
  perPage: number
}

const DEFAULT_PER_PAGE = 20
const MAX_PER_PAGE = 100 // the API's per_page cap
const DAY_MS = 24 * 60 * 60 * 1000

function positiveInt(raw: string | null, fallback: number): number {
  const n = Number(raw)
  return Number.isInteger(n) && n > 0 ? n : fallback
}

function inAreaOrder(values: readonly string[]): string[] {
  return AREAS.filter((a) => values.includes(a.value)).map((a) => a.value)
}

// The `area` URL param for a selection: "all" when cleared, else the comma list in area order.
export function areaParam(selected: readonly string[]): string {
  return selected.length === 0 ? 'all' : inAreaOrder(selected).join(',')
}

export function parseHistoryParams(params: URLSearchParams): HistoryParams {
  const rawArea = params.get('area')
  const requested = rawArea === 'all' ? [] : inAreaOrder((rawArea ?? '').split(','))
  const areas = requested.length > 0 ? requested : [...DEFAULT_AREAS]

  return {
    areas,
    set: params.get('set') || undefined,
    period: PERIODS.find((p) => p.value === params.get('period'))?.value,
    page: positiveInt(params.get('page'), 1),
    perPage: Math.min(positiveInt(params.get('per_page'), DEFAULT_PER_PAGE), MAX_PER_PAGE),
  }
}

// No selection means every area, still sent as types: bulk_file and carrier_issuer_mapping belong to no area and stay hidden.
export function resourceTypesFor(areas: readonly string[]): ResourceType[] {
  const chosen = areas.length === 0 ? AREAS : AREAS.filter((a) => areas.includes(a.value))
  return [...new Set(chosen.flatMap((a) => a.resourceTypes))]
}

export function sinceFor(period: HistoryPeriod | undefined, now: Date): string | undefined {
  const days = PERIODS.find((p) => p.value === period)?.days
  return days === undefined ? undefined : new Date(now.getTime() - days * DAY_MS).toISOString()
}

export function areasFor(resourceTypes: readonly string[]): string[] {
  return AREAS.filter((a) => a.resourceTypes.some((t) => resourceTypes.includes(t))).map((a) => a.value)
}
